import React, { useState, useEffect, useRef } from 'react';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest, putRequest, deleteRequest } from '../../services/apiService';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';

const UnitTransaction = () => {
  const [data, setData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProject, setSelectedProject] = useState(1);
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    project: 1,
    units: [],
    unitNames: {} // Changed from blockNames to unitNames
  });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch projects for dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('master/projects/');
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  // Fetch all units
  useEffect(() => {
    const fetchAllUnits = async () => {
      try {
        const response = await getRequest('master/units/');
        setUnits(response.data);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };
    fetchAllUnits();
  }, []);

  // Debounced search effect for unit transactions
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      getRequest(`transaction/unit-transaction/?project_id=${selectedProject}&search=${encodeURIComponent(search)}`)
        .then(response => {
          setData(response);
        })
        .catch(error => {
          console.error('Error fetching unit transactions:', error);
        });
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [selectedProject, search]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  // Generate dynamic fields for unit names
  const dynamicUnitNameFields = formData.units.map(unitId => {
    const unit = units.find(u => u.id === unitId);
    return {
      name: `unitName_${unitId}`,
      label: unit ? `${unit.name} (${unit.code})` : `Unit ID: ${unitId}`,
      type: 'text',
      required: true,
      placeholder: 'Enter unit name',
      value: formData.unitNames[unitId] || ''
    };
  });

  // Main form fields
  const formFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(project => ({
        label: project.name,
        value: project.id
      })),
      required: true
    },
    {
      name: 'units',
      label: 'Units',
      type: 'multiselect',
      options: units.map(unit => ({
        label: `${unit.name} (${unit.code})`,
        value: unit.id
      })),
      required: true
    },
    ...dynamicUnitNameFields
  ];

  // Handle form changes for unit names
  const handleReusableFormChange = (name, value) => {
    if (name.startsWith('unitName_')) {
      const unitId = parseInt(name.replace('unitName_', ''));
      setFormData(prev => ({
        ...prev,
        unitNames: {
          ...prev.unitNames,
          [unitId]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle submit for add/edit
  const handleReusableFormSubmit = async () => {
    try {
      if (currentItem) {
        // Edit existing entry
        await putRequest(`transaction/unit-transaction/${currentItem.id}/`, {
          project: formData.project,
          name: formData.unitNames[formData.units[0]] || '',
          unit: formData.units[0],
          status_db: 1
        });
      } else {
        // Create new entries for each selected unit
        const payload = formData.units.map(unitId => ({
          project: formData.project,
          name: formData.unitNames[unitId] || '',
          unit: unitId,
          status_db: 1
        }));
        await postRequest('transaction/unit-transaction/', payload);
      }
      // Refresh data
      const response = await getRequest(
        `transaction/unit-transaction/?project_id=${selectedProject}`
      );
      setData(response);
      setAddDialogVisible(false);
      setEditDialogVisible(false);
      setFormData({
        project: selectedProject,
        units: [],
        unitNames: {}
      });
      setCurrentItem(null);
    } catch (error) {
      console.error('Error saving unit transaction:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRequest(`transaction/unit-transaction/${currentItem.id}/`);
      setData(data.filter(item => item.id !== currentItem.id));
      setDeleteDialogVisible(false);
    } catch (error) {
      console.error('Error deleting unit transaction:', error);
    }
  };

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => {
        setCurrentItem(rowData);
        setFormData({
          project: rowData.project.id,
          units: [rowData.unit.id],
          unitNames: { [rowData.unit.id]: rowData.name }
        });
        setEditDialogVisible(true);
      }
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => {
        setCurrentItem(rowData);
        setDeleteDialogVisible(true);
      }
    }
  ];

  const columnFields = [
    { 
      field: 'name', 
      header: 'Unit Name',
      body: (rowData) => rowData.name
    },
    { 
      field: 'unit.code', 
      header: 'Unit Code',
      body: (rowData) => rowData.unit?.code || 'N/A'
    },
    { 
      field: 'project.name', 
      header: 'Project Name',
      body: (rowData) => rowData.project?.name || 'N/A'
    }
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex align-items-center">
        <label htmlFor="projectFilter" className="mr-2 font-bold">Project:</label>
        <Dropdown
          id="projectFilter"
          value={selectedProject}
          options={projects.map(project => ({
            label: project.name,
            value: project.id
          }))}
          onChange={(e) => {
            setSelectedProject(e.value);
            setFormData(prev => ({ ...prev, project: e.value }));
          }}
          placeholder="Select Project"
          style={{ width: '200px' }}
        />
      </div>

      <ReusableTable
        title="Unit Transactions"
        data={data}
        setData={setData}
        columnFields={columnFields}
        setAddNewDialogVisible={() => {
          setCurrentItem(null);
          setFormData({
            project: selectedProject,
            units: [],
            unitNames: {}
          });
          setAddDialogVisible(true);
        }}
        actionMenu={true}
        actionItems={actionItems}
        rowdelete={false}
        searchValue={search}
        onSearchChange={handleTableSearch}
      />

      {/* Add/Edit Modal with explicit wrapper */}
      {(addDialogVisible || editDialogVisible) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto animate-fade-in" style={{ width: '500px' }}>
            <ReusableForm
              headerText={currentItem ? 'Edit Unit Transaction' : 'Add New Unit Transaction'}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onChange={handleReusableFormChange}
              onSubmit={handleReusableFormSubmit}
              onHide={() => {
                setAddDialogVisible(false);
                setEditDialogVisible(false);
                setFormData({
                  project: selectedProject,
                  units: [],
                  unitNames: {}
                });
                setCurrentItem(null);
              }}
            />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onDelete={handleDelete}
        title="Delete Unit Transaction"
        description={`Are you sure you want to delete the unit transaction "${currentItem?.name}"? This action cannot be undone.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default UnitTransaction;